/**
 * 엑셀(.xlsx)·CSV·웹 표 형식 .xls 파일을 브라우저 안에서 문자열 표로 읽는다(서버로 파일을 올리지 않는다).
 * 외부 라이브러리 없이: xlsx 는 zip 안의 XML 이라 DecompressionStream 으로 풀고 첫 시트만 읽는다.
 */

export const MAX_SHEET_BYTES = 5 * 1024 * 1024;

export class SheetError extends Error {}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
const unescape = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (m, e: string) =>
  e[0] === '#' ? String.fromCodePoint(e[1]?.toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : Number(e.slice(1))) : ENTITIES[e.toLowerCase()] ?? m);

/** 한글 CSV 는 UTF-8 이 아니면 대개 EUC-KR(CP949). */
function decodeText(buf: Uint8Array, hint?: string): string {
  if (hint && /euc-?kr|ks_c_5601|cp949/i.test(hint)) return new TextDecoder('euc-kr').decode(buf);
  try { return new TextDecoder('utf-8', { fatal: true }).decode(buf); } catch { return new TextDecoder('euc-kr').decode(buf); }
}

export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const first = src.slice(0, src.indexOf('\n') >>> 0);
  const sep = (first.match(/\t/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? '\t' : ',';
  const rows: string[][] = [];
  let row: string[] = [], cell = '', quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') quoted = false; else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

/** 포털이 내려주는 '.xls' 중에는 HTML 표인 것이 많다. */
export function parseHtmlTable(html: string): string[][] {
  const table = html.match(/<table[\s\S]*?<\/table>/i)?.[0] ?? html;
  return [...table.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)].map((tr) =>
    [...tr[1]!.matchAll(/<t[dh]([^>]*)>([\s\S]*?)<\/t[dh]>/gi)].flatMap((td) => {
      const text = unescape(td[2]!.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim();
      const span = Number(td[1]!.match(/colspan\s*=\s*"?(\d+)/i)?.[1] ?? 1);
      return [text, ...Array<string>(Math.max(0, Math.min(span, 50) - 1)).fill('')];
    }));
}

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** zip 의 중앙 디렉터리를 읽어 필요한 파일만 푼다. */
async function unzip(buf: Uint8Array, want: (name: string) => boolean): Promise<Map<string, string>> {
  const v = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let end = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65_557); i--) if (v.getUint32(i, true) === 0x06054b50) { end = i; break; }
  if (end < 0) throw new SheetError('엑셀 파일을 열 수 없습니다(손상되었거나 xlsx 가 아닙니다).');
  const count = v.getUint16(end + 10, true);
  let p = v.getUint32(end + 16, true);
  const out = new Map<string, string>();
  const utf8 = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (v.getUint32(p, true) !== 0x02014b50) throw new SheetError('엑셀 파일 구조를 읽지 못했습니다.');
    const method = v.getUint16(p + 10, true), size = v.getUint32(p + 20, true);
    const nameLen = v.getUint16(p + 28, true), extraLen = v.getUint16(p + 30, true), commentLen = v.getUint16(p + 32, true);
    const local = v.getUint32(p + 42, true);
    const name = utf8.decode(buf.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (!want(name)) continue;
    const start = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true);
    const raw = buf.subarray(start, start + size);
    if (method !== 0 && method !== 8) throw new SheetError('지원하지 않는 압축 방식의 엑셀 파일입니다.');
    out.set(name, utf8.decode(method === 8 ? await inflate(raw) : raw));
  }
  return out;
}

const colIndex = (ref: string) => [...(ref.match(/^[A-Z]+/)?.[0] ?? 'A')].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0) - 1;
const textOf = (xml: string) => [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => unescape(m[1]!)).join('');
/** 큰 숫자 승인번호가 지수 표기('1.23E+11')로 저장된 경우를 되돌린다. */
const numText = (s: string) => (/e[+-]?\d+$/i.test(s) && Number.isFinite(Number(s)) && Math.abs(Number(s)) >= 1e6 ? BigInt(Math.round(Number(s))).toString() : s);

export async function parseXlsx(buf: Uint8Array): Promise<string[][]> {
  const files = await unzip(buf, (n) => n === 'xl/workbook.xml' || n === 'xl/_rels/workbook.xml.rels' || n === 'xl/sharedStrings.xml' || /^xl\/worksheets\/[^/]+\.xml$/.test(n));
  // 첫 시트: workbook 의 첫 <sheet> → rels 의 Target
  const rid = files.get('xl/workbook.xml')?.match(/<sheet\b[^>]*\br:id="([^"]+)"/)?.[1];
  const target = [...(files.get('xl/_rels/workbook.xml.rels') ?? '').matchAll(/<Relationship\b[^>]*>/g)]
    .map((m) => m[0]).find((tag) => tag.match(/\bId="([^"]+)"/)?.[1] === rid)?.match(/\bTarget="([^"]+)"/)?.[1];
  const path = target ? (target.startsWith('/') ? target.slice(1) : `xl/${target}`) : [...files.keys()].find((n) => n.startsWith('xl/worksheets/'));
  const sheet = path && files.get(path);
  if (!sheet) throw new SheetError('엑셀 파일에서 시트를 찾지 못했습니다.');
  const shared = [...(files.get('xl/sharedStrings.xml') ?? '').matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textOf(m[1]!));

  const rows: string[][] = [];
  for (const rm of sheet.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const r = Number(rm[1]!.match(/\br="(\d+)"/)?.[1] ?? rows.length + 1) - 1;
    if (r > 20_000) break;
    const row: string[] = [];
    let next = 0;
    for (const cm of (rm[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = cm[1]!, body = cm[2] ?? '';
      const ref = attrs.match(/\br="([A-Z]+)\d+"/)?.[1];
      const c = ref ? colIndex(ref) : next;
      next = c + 1;
      if (c > 200) continue;
      const type = attrs.match(/\bt="(\w+)"/)?.[1];
      const value = unescape(body.match(/<v>([\s\S]*?)<\/v>/)?.[1] ?? '');
      row[c] = type === 's' ? shared[Number(value)] ?? '' : type === 'inlineStr' ? textOf(body) : type === 'b' ? (value === '1' ? 'TRUE' : 'FALSE') : numText(value);
    }
    rows[r] = Array.from(row, (x) => x ?? '');
  }
  return Array.from(rows, (x) => x ?? []);
}

/** 파일 종류는 확장자가 아니라 앞부분 바이트로 가린다. */
export async function readTable(file: Blob & { name?: string }): Promise<string[][]> {
  if (file.size > MAX_SHEET_BYTES) throw new SheetError('파일이 너무 큽니다(5MB 까지). 한 달 치만 내려받아 올려 주세요.');
  const buf = new Uint8Array(await file.arrayBuffer());
  if (buf[0] === 0x50 && buf[1] === 0x4b) return parseXlsx(buf);
  if (buf[0] === 0xd0 && buf[1] === 0xcf && buf[2] === 0x11 && buf[3] === 0xe0) {
    throw new SheetError("엑셀 97-2003 형식(.xls)은 읽을 수 없습니다. 엑셀에서 '다른 이름으로 저장 → Excel 통합 문서(.xlsx)'로 저장해 올려 주세요.");
  }
  const head = new TextDecoder('latin1').decode(buf.subarray(0, 2048));
  const text = decodeText(buf, head.match(/charset\s*=\s*["']?([\w-]+)/i)?.[1]);
  if (/^\s*(?:﻿)?\s*</.test(text) && /<table/i.test(text)) return parseHtmlTable(text);
  return parseCsv(text);
}
